import { Component, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { BackofficeSessionService } from './core/backoffice-session.service';
import { I18nService } from './core/i18n.service';
import { SessionService } from './core/session.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  private readonly router = inject(Router);
  protected readonly session = inject(SessionService);
  protected readonly backofficeSession = inject(BackofficeSessionService);
  protected readonly i18n = inject(I18nService);
  protected readonly environmentBadgeLabel = environment.environmentBadgeLabel;

  protected playerIdentityLabel(): string {
    const identity = this.session.identity();
    return identity?.pseudo ? this.i18n.t('identity.player', { value: identity.pseudo }) : this.i18n.t('identity.player', { value: '---' });
  }

  protected backofficeIdentityLabel(): string {
    const identity = this.backofficeSession.identity();
    if (!identity) {
      return this.i18n.t('identity.bo', { email: '---', role: 'backoffice' });
    }
    const primaryRole = identity.roles[0] ?? 'backoffice';
    return this.i18n.t('identity.bo', { email: identity.email, role: primaryRole });
  }

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  protected setLanguage(lang: 'fr' | 'en'): void {
    this.i18n.setLanguage(lang);
  }

  protected isLanguage(lang: 'fr' | 'en'): boolean {
    return this.i18n.isLanguage(lang);
  }

  protected hasPlayerSession(): boolean {
    return this.session.isAuthenticated();
  }

  protected hasBackofficeSession(): boolean {
    return this.backofficeSession.isAuthenticated();
  }

  protected hasAnySession(): boolean {
    return this.hasPlayerSession() || this.hasBackofficeSession();
  }

  protected hasBothSessions(): boolean {
    return this.hasPlayerSession() && this.hasBackofficeSession();
  }

  protected hasEnvironmentBadge(): boolean {
    return Boolean(this.environmentBadgeLabel);
  }

  protected logout(): void {
    this.session.clearToken();
    void this.router.navigateByUrl('/player/login');
  }

  protected backofficeLogout(): void {
    this.backofficeSession.clearToken();
    void this.router.navigateByUrl('/backoffice/session');
  }
}
